
import { toast } from "react-toastify";

/**
 * Simple API client for handling all API requests consistently
 */
const apiClient = {
  /**
   * Get authentication token
   * @returns {string} auth token
   */
  getToken() {
    return localStorage.getItem("token");
  },

  /**
   * Get API endpoint URL (main, operational, or new)
   * @param {boolean|string} useOperational - Whether to use operational API, or 'new' for NEW_URL
   * @returns {string} API root URL
   */
  // getApiRoot(useOperational = false) {
  //   if (useOperational === 'new') {
  //     return process.env.REACT_APP_GUI_API_URL;
  //   }
  //   return useOperational
  //     ? process.env.REACT_APP_API_ROOT_OPERATIONAL
  //     : process.env.REACT_APP_API_ROOT;
  // },
getApiRoot(useOperational = false) {
  if (useOperational === 'new') {
    return process.env.REACT_APP_GUI_API_URL;
  }
  if (useOperational === 'execution') {
    return process.env.REACT_APP_SERVER_EXECUTION;
  }
  return useOperational
    ? process.env.REACT_APP_API_ROOT_OPERATIONAL
    : process.env.REACT_APP_API_ROOT;
},
  /**
   * Make a GET request
   *
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async get(endpoint, options = {}) {
    const {
      useOperational = false,
      params = {},
      showToast = true,
      showLogs = true,
    } = options;

    // Build URL with query parameters
    const apiRoot = this.getApiRoot(useOperational);
    let url = `${apiRoot}${endpoint}`;

    // Add query parameters if any
    if (Object.keys(params).length > 0) {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (typeof value === "object") {
            queryParams.append(key, JSON.stringify(value));
          } else {
            queryParams.append(key, value);
          }
        }
      });
      url += `?${queryParams.toString()}`;
    }

    return this.request(url, {
      method: "GET",
      showToast,
      showLogs,
    });
  },

  /**
   * Make a POST request
   *
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object|FormData} data - Request data
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async post(endpoint, data = {}, options = {}) {
    const {
      useOperational = false,
      isFormData = false,
      showToast = true,
      showLogs = true,
    } = options;

    const apiRoot = this.getApiRoot(useOperational);
    const url = `${apiRoot}${endpoint}`;

    return this.request(url, {
      method: "POST",
      body: data,
      isFormData,
      showToast,
      showLogs,
    });
  },

  /**
   * Make a PUT request
   *
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object|FormData} data - Request data
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async put(endpoint, data = {}, options = {}) {
    const {
      useOperational = false,
      isFormData = false,
      showToast = true,
      showLogs = true,
    } = options;

    const apiRoot = this.getApiRoot(useOperational);
    const url = `${apiRoot}${endpoint}`;

    return this.request(url, {
      method: "PUT",
      body: data,
      isFormData,
      showToast,
      showLogs,
    });
  },

  /**
   * Make a PATCH request
   *
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object|FormData} data - Request data
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async patch(endpoint, data = {}, options = {}) {
    const {
      useOperational = false,
      isFormData = false,
      showToast = true,
      showLogs = true,
    } = options;

    const apiRoot = this.getApiRoot(useOperational);
    const url = `${apiRoot}${endpoint}`;

    return this.request(url, {
      method: "PATCH",
      body: data,
      isFormData,
      showToast,
      showLogs,
    });
  },

  /**
   * Make a DELETE request
   *
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async delete(endpoint, options = {}) {
    const {
      useOperational = false,
      body = null,
      showToast = true,
      showLogs = true,
    } = options;

    const apiRoot = this.getApiRoot(useOperational);
    const url = `${apiRoot}${endpoint}`;

    return this.request(url, {
      method: "DELETE",
      body,
      showToast,
      showLogs,
    });
  },

  /**
   * Make an HTTP request
   *
   * @param {string} url - Full URL
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async request(url, options = {}) {
    const {
      method = "GET",
      body = null,
      isFormData = false,
      showToast = true,
      showLogs = true,
    } = options;

    const token = this.getToken();

    if (showLogs) {
      console.group(`API Request: ${method} ${url}`);
      console.log("Request Details:", {
        url,
        method,
        isFormData,
        token: token ? "Token present" : "No token",
        body: body
          ? isFormData
            ? "FormData"
            : JSON.stringify(body, null, 2)
          : null,
      });
    }

    const headers = {};

    // Add Authorization header if token exists
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    // Add Content-Type for non-FormData requests
    if (!isFormData && method !== "GET" && !(body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    const fetchOptions = {
      method,
      headers,
    };

    if (body && method !== "GET") {
      fetchOptions.body =
        isFormData || body instanceof FormData ? body : JSON.stringify(body);
    }

    try {
      const response = await fetch(url, fetchOptions);

      if (showLogs) {
        console.log("Response Status:", response.status);
      }

      // Handle error response
      if (!response.ok) {
        const contentType = response.headers.get("Content-Type") || "";
        let errorDetails = {};

        try {
          if (contentType.includes("application/json")) {
            errorDetails = await response.json();
          } else {
            errorDetails.message = await response.text();
          }
        } catch (parseError) {
          errorDetails.message = "Could not parse error response";
        }

        if (showLogs) {
          console.error("Error Response:", errorDetails);
          console.groupEnd();
        }

        const errorMessage = errorDetails.message || "Error with API call";

        if (showToast) {
          toast.error(errorMessage);
        }

        throw new Error(errorMessage);
      }

      // Parse response based on content type
      const contentType = response.headers.get("Content-Type") || "";
      let responseData;

      if (contentType.includes("application/json")) {
        responseData = await response.json();
        if (showLogs) {
          console.log("Response Data:", responseData);
        }
      } else {
        responseData = await response.text();
        if (showLogs) {
          console.log("Response Text:", responseData);
        }
      }

      if (showLogs) {
        console.groupEnd();
      }

      return responseData;
    } catch (error) {
      if (showLogs) {
        console.error("Fetch Error:", error);
        console.groupEnd();
      }

      if (showToast) {
        toast.error(
          error.message || "An error occurred while processing your request."
        );
      }

      throw error;
    }
  },

  /**
   * Upload a file
   *
   * @param {string} endpoint - API endpoint
   * @param {File} file - File to upload
   * @param {Object} additionalData - Additional form data
   * @param {Object} options - Request options
   * @returns {Promise<any>} Response data
   */
  async uploadFile(endpoint, file, additionalData = {}, options = {}) {
    const formData = new FormData();
    formData.append("file", file);

    Object.entries(additionalData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === "object" && !(value instanceof File)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      }
    });

    return this.post(endpoint, formData, {
      ...options,
      isFormData: true,
    });
  },
};

export default apiClient;